// ═══════════════ BARDOWN SCOUT — the secret report ═══════════════
// 1. scanBets: every bet the betting screens offer, for every upcoming game of every sport,
//    built exactly like the buttons build it and priced by the odds engine (quoteBet).
// 2. Each bet gets a win chance (the odds engine's own model) and, where the scout's analysis
//    (records, form, goalies, pitchers…) has an opinion, a "value": how much the scout thinks
//    the bet is worth more than it pays.
// 3. rankBets: Value / Safest / Long shots, a few per game so one game can't fill the list.
import { quoteBet, nhlMarkets, nhlTeamTotals, nhlPlayerMarkets, mlbMarkets, nbaMarkets, nbaPlayerMarkets, nflMarkets, nflPlayerMarkets, f1Markets, NBA_MARGIN_BANDS, EDGE } from './odds';

const NOT_BETTABLE = ['FINAL', 'OFF', 'LIVE', 'CRIT', 'PPD'];
const HOUR = 3600 * 1000;
// how far ahead the scout looks: tonight's / tomorrow's games, the coming NFL week and F1 race
const WINDOW = { nhl: 40 * HOUR, mlb: 40 * HOUR, nba: 40 * HOUR, nfl: 8 * 24 * HOUR, f1: 8 * 24 * HOUR };
// bets priced as long shots (shorter margin) — see priceLong in odds.js
const LONG = new Set(['exact_score', 'mlb_batter_cycle', 'nfl_first_td', 'first_goal', 'winning_goal', 'nba_top_scorer', 'nba_first_basket', 'f1_winner', 'f1_pole', 'f1_fastest_lap']);

const normCdf = (x) => {
  const t = 1 / (1 + 0.2316419 * Math.abs(x));
  const d = 0.3989423 * Math.exp(-x * x / 2);
  const p = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
  return x > 0 ? 1 - p : p;
};
const normInv = (p) => {   // good enough for 0.02–0.98
  let lo = -5, hi = 5;
  for (let i = 0; i < 50; i++) { const mid = (lo + hi) / 2; if (normCdf(mid) < p) lo = mid; else hi = mid; }
  return (lo + hi) / 2;
};

const startsAt = (g) => new Date(g.game_time || g.race_time || 0).getTime();
const upcoming = (g, sport, now) => {
  if (!g || NOT_BETTABLE.includes(g.status)) return false;
  if (sport === 'f1') return g.bettable && g.status === 'FUT';
  if (sport === 'nfl' && (g.status !== 'FUT' || !g.model)) return false;
  const t = startsAt(g);
  return !t || (t > now - 10 * 60 * 1000 && t - now < WINDOW[sport]);
};

// ── 1. every bet ──
// data: { games, mlbGames, nbaGames, nflGames, f1Races, rosterPlayers, gameInjuries, mlbRosters, nbaRosters, nflProps, playerRates }
export function scanBets(data, ctx, now = Date.now()) {
  const out = [];
  const seen = new Set();
  const push = (sport, game, betType, selection, details = {}) => {
    const key = `${game.id}-${betType}-${selection}-${JSON.stringify(details)}`;
    if (seen.has(key)) return;
    seen.add(key);
    let odds = null;
    try { odds = quoteBet(game, betType, selection, details, ctx); } catch (e) { odds = null; }
    if (!(odds > 1.01)) return;
    const prob = Math.min(0.99, (LONG.has(betType) ? 0.9 : 1 - EDGE) / odds);
    out.push({ key, sport, game, betType, selection, details, odds, prob });
  };
  const ou = (sport, g, bt, line, extra = {}) => ['over', 'under'].forEach((s) => push(sport, g, bt, s, { ...extra, type: s === 'over' ? 'Over' : 'Under', line }));
  const sides = ['away', 'home'];

  // NHL
  (data.games || []).filter((g) => upcoming(g, 'nhl', now)).forEach((g) => {
    sides.forEach((s) => push('nhl', g, 'winner', s));
    push('nhl', g, 'puckline', 'away', { team: g.away_team, line: 1.5 });
    push('nhl', g, 'puckline', 'home', { team: g.home_team, line: -1.5 });
    const hm = nhlMarkets(g);
    (hm?.totals || []).forEach(({ line }) => ou('nhl', g, 'total', line));
    sides.forEach((s) => push('nhl', g, 'team_first', s));
    ['yes', 'no'].forEach((s) => push('nhl', g, 'both_score', s));
    ['1st', '2nd', '3rd'].forEach((period) => {
      push('nhl', g, 'period_winner', 'away', { period, pick: g.away_team });
      push('nhl', g, 'period_winner', 'draw', { period, pick: 'Draw' });
      push('nhl', g, 'period_winner', 'home', { period, pick: g.home_team });
    });
    ['overtime', 'shutout', 'fighting'].forEach((bt) => push('nhl', g, bt, 'yes'));
    const ht = nhlTeamTotals(g, ctx.nhlTeams);
    if (ht?.pim) ou('nhl', g, 'penalty_mins', ht.pim.line);
    (ht?.shots || []).forEach(({ line }) => ou('nhl', g, 'total_shots', line));
    [[3, 2], [4, 2], [3, 1], [4, 3], [2, 1], [5, 3], [2, 3], [1, 2], [1, 3]].forEach(([h, a]) => push('nhl', g, 'exact_score', 'home', { home: h, away: a }));
    // players: each team's top 9 healthy skaters by points
    const injured = new Set([...(data.gameInjuries?.[g.home_abbrev] || []), ...(data.gameInjuries?.[g.away_abbrev] || [])].map((i) => String(i.name || '').toLowerCase()));
    [g.home_abbrev, g.away_abbrev].forEach((team) => {
      (data.rosterPlayers || [])
        .filter((p) => p.team === team && p.position !== 'G' && p.position !== 'T' && !injured.has(String(p.name).toLowerCase()))
        .sort((a, b) => (b.points || 0) - (a.points || 0)).slice(0, 9)
        .forEach((p) => {
          const d = { playerId: p.id, playerName: p.name };
          ['first_goal', 'anytime_goal', 'winning_goal', 'player_points'].forEach((bt) => push('nhl', g, bt, 'player', d));
          const pm = nhlPlayerMarkets(g, p, ctx.nhlRates);
          (pm?.shots || []).forEach(({ line }) => ou('nhl', g, 'player_shots', line, d));
        });
    });
  });

  // MLB
  (data.mlbGames || []).filter((g) => upcoming(g, 'mlb', now)).forEach((g) => {
    sides.forEach((s) => push('mlb', g, 'winner', s));
    push('mlb', g, 'runline', 'away', { team: g.away_abbrev, line: 1.5 });
    push('mlb', g, 'runline', 'home', { team: g.home_abbrev, line: -1.5 });
    const mm = mlbMarkets(g);
    if (mm) ou('mlb', g, 'total', mm.total.line);
    sides.forEach((s) => [0.5, 1.5, 2.5].forEach((line) => push('mlb', g, 'mlb_team_hr', s, { team: g[`${s}_abbrev`], line })));
    [[3, 1], [4, 2], [5, 3], [3, 2], [2, 1], [6, 4], [1, 3], [2, 4], [3, 5]].forEach(([h, a]) => push('mlb', g, 'exact_score', 'home', { home: h, away: a }));
    if (g.game_type !== 'S') {
      sides.forEach((s) => {
        const id = g[`${s}_pitcher_id`], ks = mm?.ks(s === 'home');
        if (id && ks) ou('mlb', g, 'mlb_pitcher_ks', ks.line, { playerId: id, playerName: g[`${s}_pitcher`] });
      });
      const CATS = { mlb_batter_hr: [0.5, 1.5, 2.5], mlb_batter_rbi: [0.5, 1.5, 2.5, 3.5], mlb_batter_sb: [0.5, 1.5], mlb_batter_1b: [0.5], mlb_batter_2b: [0.5], mlb_batter_3b: [0.5], mlb_batter_ab: [3.5, 4.5] };
      [g.away_team_id, g.home_team_id].forEach((tid) => {
        (data.mlbRosters?.[String(tid)]?.batters || []).slice(0, 13).forEach((p) => {
          Object.entries(CATS).forEach(([bt, lines]) => lines.forEach((line) => push('mlb', g, bt, 'over', { playerId: p.id, playerName: p.name, line })));
          push('mlb', g, 'mlb_batter_cycle', 'yes', { playerId: p.id, playerName: p.name });
        });
      });
    }
  });

  // NBA
  (data.nbaGames || []).filter((g) => upcoming(g, 'nba', now)).forEach((g) => {
    sides.forEach((s) => push('nba', g, 'winner', s));
    const nm = nbaMarkets(g);
    if (nm) {
      const homeLine = -nm.spread.line;
      push('nba', g, 'spread', 'away', { team: g.away_abbrev, line: -homeLine });
      push('nba', g, 'spread', 'home', { team: g.home_abbrev, line: homeLine });
      ou('nba', g, 'total', nm.total.line);
      sides.forEach((s) => ['over', 'under'].forEach((sel) => push('nba', g, 'nba_team_total', sel, { team: s, teamName: g[`${s}_team`], line: nm.teamTotal[s].line })));
      ['q1', 'h1'].forEach((period) => sides.forEach((s) => push('nba', g, 'nba_quarter_winner', s, { period, teamName: g[`${s}_team`] })));
      ['over', 'under'].forEach((sel) => push('nba', g, 'nba_half_total', sel, { line: nm.halfTotal.line }));
      sides.forEach((s) => NBA_MARGIN_BANDS.forEach((band) => push('nba', g, 'nba_margin', s, { band, teamName: g[`${s}_team`] })));
      ['yes', 'no'].forEach((s) => { push('nba', g, 'nba_overtime', s); push('nba', g, 'nba_both_110', s); });
      ['odd', 'even'].forEach((s) => push('nba', g, 'nba_odd_even', s));
    }
    // players: the regulars (most minutes) of each team
    const rates = data.playerRates?.nba || {};
    const listOf = (abbrev, n) => (data.nbaRosters?.[abbrev]?.players || [])
      .filter((p) => nbaPlayerMarkets(rates[String(p.id)]))
      .sort((a, b) => (rates[String(b.id)]?.min || 0) - (rates[String(a.id)]?.min || 0)).slice(0, n);
    const STAT = { nba_player_pts: 'pts', nba_player_reb: 'reb', nba_player_ast: 'ast', nba_player_3pm: 'tpm', nba_player_stl: 'stl', nba_player_blk: 'blk' };
    [g.away_abbrev, g.home_abbrev].forEach((abbrev) => listOf(abbrev, 12).forEach((p) => {
      const mk = nbaPlayerMarkets(rates[String(p.id)]);
      const d = { playerId: p.id, playerName: p.name };
      Object.entries(STAT).forEach(([bt, k]) => { if (mk[k]) ['over', 'under'].forEach((sel) => push('nba', g, bt, sel, { ...d, line: mk[k].line })); });
      if (mk.pra) ['over', 'under'].forEach((sel) => push('nba', g, 'nba_player_pra', sel, { ...d, line: mk.pra.line }));
      ['yes', 'no'].forEach((sel) => { push('nba', g, 'nba_double_double', sel, d); push('nba', g, 'nba_triple_double', sel, d); });
    }));
    const field = [g.away_abbrev, g.home_abbrev].flatMap((a) => listOf(a, 9));
    if (field.length >= 6) {
      const ids = field.map((p) => String(p.id));
      field.forEach((p) => ['nba_top_scorer', 'nba_first_basket'].forEach((bt) => push('nba', g, bt, String(p.id), { playerId: String(p.id), playerName: p.name, field: ids })));
    }
  });

  // NFL
  (data.nflGames || []).filter((g) => upcoming(g, 'nfl', now)).forEach((g) => {
    sides.forEach((s) => push('nfl', g, 'winner', s));
    const mk = nflMarkets(g);
    if (mk) {
      push('nfl', g, 'spread', 'away', { team: g.away_abbrev, line: mk.spread.line });
      push('nfl', g, 'spread', 'home', { team: g.home_abbrev, line: -mk.spread.line });
      ou('nfl', g, 'total', mk.total.line);
    }
    push('nfl', g, 'nfl_half_winner', 'away', { pick: g.away_team });
    push('nfl', g, 'nfl_half_winner', 'tie', { pick: 'Tie' });
    push('nfl', g, 'nfl_half_winner', 'home', { pick: g.home_team });
    ['yes', 'no'].forEach((s) => push('nfl', g, 'nfl_both_20', s));
    push('nfl', g, 'nfl_overtime', 'yes');
    const pr = data.nflProps?.[g.id];
    if (pr) [...(pr.away || []), ...(pr.home || [])].forEach((p) => {
      const pm = nflPlayerMarkets(g, p);
      if (!pm) return;
      const d = { playerId: p.id, playerName: p.name };
      if (pm.anytime) push('nfl', g, 'nfl_anytime_td', 'player', d);
      if (pm.first) push('nfl', g, 'nfl_first_td', 'player', d);
      [['pass', 'nfl_pass_yds'], ['rush', 'nfl_rush_yds'], ['rec', 'nfl_rec_yds']].forEach(([k, bt]) => {
        if (pm[k]) ['over', 'under'].forEach((sel) => push('nfl', g, bt, sel, { ...d, line: pm[k].line, type: sel === 'over' ? 'Over' : 'Under' }));
      });
    });
  });

  // F1
  (data.f1Races || []).filter((r) => upcoming(r, 'f1', now)).forEach((r) => {
    const mk = f1Markets(r);
    if (!mk) return;
    const surname = (n) => String(n || '').trim().split(' ').pop();
    (mk.drivers || []).forEach((d) => {
      const det = { driver: surname(d.name) };
      ['f1_winner', 'f1_top3', 'f1_top6', 'f1_top10', 'f1_fastest_lap'].forEach((bt) => push('f1', r, bt, d.code, det));
      if (mk.poleOpen) push('f1', r, 'f1_pole', d.code, det);
    });
    const byCode = Object.fromEntries((mk.drivers || []).map((d) => [d.code, d]));
    (mk.h2h || []).forEach((x) => {
      const A = surname(byCode[x.a]?.name || x.a), B = surname(byCode[x.b]?.name || x.b);
      push('f1', r, 'f1_h2h', x.a, { driver: A, vs: x.b, vsName: B });
      push('f1', r, 'f1_h2h', x.b, { driver: B, vs: x.a, vsName: A });
    });
    (mk.constructors || []).forEach((c) => push('f1', r, 'f1_constructor', c.team, { team: c.team }));
  });

  return out;
}

// ── 2. the scout's opinion ──
// Two sources, per NHL / MLB / NBA / NFL game:
//  • Vegas: the sportsbook's moneyline, spread and total (DraftKings via the server, report.market), with
//    the book's cut removed. The best single guess there is — it gets most of the weight.
//  • the server's own model (report.nhl / mlb / nba): its win chance for each side and expected total.
// Where the blend disagrees with the site's price, that's value.
const TOTAL_SD = { nhl: 2.4, mlb: 4.4, nba: 18, nfl: 13.5 };
// a model total outside these is a stats glitch (e.g. 0 goals on opening week, before any game): no opinion
const TOTAL_OK = { nhl: [3.5, 9.5], mlb: [4, 16], nba: [180, 270] };
const MARGIN_SD = { nba: 13.5, nfl: 13.5 };
const VEGAS_W = 0.7;   // Vegas vs the model, when both have an opinion
// ESPN's abbreviations -> the site's
const ALIAS = {
  nhl: { NJ: 'NJD', TB: 'TBL', SJ: 'SJS', LA: 'LAK', UTAH: 'UTA' },
  nba: { GS: 'GSW', NY: 'NYK', NO: 'NOP', SA: 'SAS', UTAH: 'UTA', WSH: 'WAS' },
  mlb: { ARI: 'AZ', CHW: 'CWS', WSH: 'WSH', ATH: 'ATH' },
  nfl: {},
};
const fair = (a, b) => (1 / a) / (1 / a + 1 / b);   // a's chance with the book's cut removed
const matchLine = (lines, sport, g) => {
  const al = (x) => ALIAS[sport]?.[x] || x;
  const t = startsAt(g);
  return lines.find((l) => l.sport === sport && al(l.home) === g.home_abbrev && al(l.away) === g.away_abbrev
    && (!t || Math.abs(new Date(l.start).getTime() - t) < 6 * HOUR));
};
export function scoutSignals(report, { games = [], mlbGames = [], nbaGames = [], nflGames = [] }) {
  const map = {};
  const find = (list, p) => list.find((g) => String(g.id) === String(p.gameId))
    || list.find((g) => g.home_abbrev === p.home && g.away_abbrev === p.away);
  [['nhl', games], ['mlb', mlbGames], ['nba', nbaGames]].forEach(([sport, list]) => {
    (report?.[sport] || []).forEach((p) => {
      const g = find(list, p);
      if (!g) return;
      const est = p.totalGoals?.estimated ?? p.totalRuns?.estimated ?? p.totalPoints?.estimated;
      const total = est >= TOTAL_OK[sport][0] && est <= TOTAL_OK[sport][1] ? est : null;
      const conf = p.homeConfidence ?? (p.pick === 'home' ? p.confidence : 100 - p.confidence);
      // the model's win chance, kept in a sane range (its stats are noisy, especially early in a season)
      const pHome = conf > 0 && conf < 100 ? Math.min(0.85, Math.max(0.15, conf / 100)) : null;
      map[String(g.id)] = { sport, pHome, total, scout: p };
    });
  });
  const lines = report?.market || [];
  [['nhl', games], ['mlb', mlbGames], ['nba', nbaGames], ['nfl', nflGames]].forEach(([sport, list]) => {
    (list || []).forEach((g) => {
      const l = matchLine(lines, sport, g);
      if (!l) return;
      const v = {};
      if (l.ml) v.pHome = fair(l.ml.home, l.ml.away);
      if (l.total) v.total = { line: l.total.line, pOver: fair(l.total.over, l.total.under) };
      if (l.spread) v.spread = { homeLine: l.spread.line, pHome: fair(l.spread.home, l.spread.away) };
      map[String(g.id)] = { ...(map[String(g.id)] || { sport }), vegas: v, book: l.book };
    });
  });
  return map;
}

// NHL goals are counted, not a bell curve: P(total > line) for an expected total (Poisson), and back
const poisOver = (mean, line) => { let p = Math.exp(-mean), cdf = 0; for (let k = 0; k <= Math.floor(line); k++) { cdf += p; p *= mean / (k + 1); } return 1 - cdf; };
const poisMean = (line, pOver) => { let lo = 1, hi = 15; for (let i = 0; i < 40; i++) { const mid = (lo + hi) / 2; if (poisOver(mid, line) < pOver) lo = mid; else hi = mid; } return (lo + hi) / 2; };
// Vegas' expected total from its line + over chance, and expected home margin from its spread
const vegasTotal = (v, sport) => (sport === 'nhl' ? poisMean(v.line, v.pOver)
  : v.line + TOTAL_SD[sport] * normInv(Math.min(0.97, Math.max(0.03, v.pOver))));
const vegasMargin = (v, sport) => -v.homeLine + MARGIN_SD[sport] * normInv(Math.min(0.97, Math.max(0.03, v.pHome)));
const mix = (vegas, model) => (vegas == null ? model : model == null ? vegas : VEGAS_W * vegas + (1 - VEGAS_W) * model);

// the scout's chance for a bet → { chance, vegas } (Vegas' own chance, when it prices that market), or null
function scoutChance(bet, sig) {
  if (!sig) return null;
  const { betType: bt, selection: sel, details: d, sport } = bet;
  const v = sig.vegas || {};
  if (bt === 'winner') {
    const p = mix(v.pHome, sig.pHome);
    if (p == null) return null;
    const side = (x) => (x == null ? null : sel === 'home' ? x : 1 - x);
    return { chance: side(p), vegas: side(v.pHome) };
  }
  if (bt === 'total' && TOTAL_SD[sport]) {
    const mean = mix(v.total ? vegasTotal(v.total, sport) : null, sig.total);
    if (mean == null) return null;
    const over = (m) => (sport === 'nhl' ? poisOver(m, d.line) : 1 - normCdf((d.line - m) / TOTAL_SD[sport]));
    const side = (x) => (sel === 'over' ? x : 1 - x);
    // same line as Vegas: its own chance; otherwise moved along the curve
    const vg = v.total ? (v.total.line === d.line ? v.total.pOver : over(vegasTotal(v.total, sport))) : null;
    return { chance: side(over(mean)), vegas: vg == null ? null : side(vg) };
  }
  if (bt === 'spread' && MARGIN_SD[sport]) {
    const modelMargin = sig.pHome != null ? MARGIN_SD[sport] * normInv(Math.min(0.97, Math.max(0.03, sig.pHome))) : null;
    const vm = v.spread ? vegasMargin(v.spread, sport) : (v.pHome != null ? MARGIN_SD[sport] * normInv(Math.min(0.97, Math.max(0.03, v.pHome))) : null);
    const margin = mix(vm, modelMargin);
    if (margin == null) return null;
    const need = sel === 'home' ? -d.line : d.line;
    const cover = (m) => { const pHome = 1 - normCdf((need - m) / MARGIN_SD[sport]); return sel === 'home' ? pHome : 1 - pHome; };
    return { chance: cover(margin), vegas: vm == null ? null : cover(vm) };
  }
  // puck line / run line (±1.5): only when Vegas has the same line (the home team giving 1.5)
  if ((bt === 'puckline' || bt === 'runline') && v.spread && v.spread.homeLine === -1.5) {
    const p = sel === 'home' ? v.spread.pHome : 1 - v.spread.pHome;
    return { chance: p, vegas: p };
  }
  return null;
}

export function scoreBets(bets, signals) {
  return bets.map((b) => {
    const s = scoutChance(b, signals[String(b.game.id)]);
    // with Vegas: the blend IS the chance (the site's own price isn't a second opinion); model only: half-trusted
    const chance = s == null ? b.prob : s.vegas != null ? s.chance : 0.5 * s.chance + 0.5 * b.prob;
    return { ...b, scout: s?.chance ?? null, vegas: s?.vegas ?? null, chance, value: s == null ? null : chance * b.odds - 1 };
  });
}

// ── 3. the lists ──
// variety: at most 3 bets per game and 3 of the same market, so one game or one prop can't fill the list
const PER_GAME = 3, PER_MARKET = 3;
export function rankBets(scored, mode, sport = 'all', limit = 25) {
  let list = scored.filter((b) => sport === 'all' || b.sport === sport);
  if (mode === 'value') list = list.filter((b) => b.value != null && b.value > 0.01).sort((a, b) => b.value - a.value);
  else if (mode === 'safest') list = list.filter((b) => b.odds >= 1.4).sort((a, b) => b.chance - a.chance || b.odds - a.odds);
  else list = list.filter((b) => b.odds >= 5 && b.odds <= 30).sort((a, b) => b.chance - a.chance);
  const perGame = {}, perMarket = {};
  return list.filter((b) => {
    const g = String(b.game.id), m = b.betType;
    if ((perGame[g] || 0) >= PER_GAME || (perMarket[m] || 0) >= PER_MARKET) return false;
    perGame[g] = (perGame[g] || 0) + 1;
    perMarket[m] = (perMarket[m] || 0) + 1;
    return true;
  }).slice(0, limit);
}

// ── 4. parlays: ready-made combos, one leg per game (legs from the same game move together,
//    so their chances can't simply be multiplied). Chance = product of the legs' chances.
const pickLegs = (pool, n) => {
  const games = new Set(), markets = new Set(), legs = [];
  for (const b of pool) {
    if (legs.length === n) break;
    const g = String(b.game.id);
    if (games.has(g) || markets.has(b.betType)) continue;   // one per game, and a mix of markets
    games.add(g);
    markets.add(b.betType);
    legs.push(b);
  }
  return legs.length === n ? legs : null;
};
export function buildParlays(scored, sport = 'all') {
  const pool = scored.filter((b) => sport === 'all' || b.sport === sport);
  const byChance = (a, b) => b.chance - a.chance;
  const inRange = (lo, hi) => pool.filter((b) => b.odds >= lo && b.odds <= hi).sort(byChance);
  const value = pool.filter((b) => b.value != null && b.value > 0.01).sort((a, b) => b.value - a.value);
  const recipes = [
    { id: 'safe2', en: 'Safe double', fr: 'Double sûr', legs: pickLegs(inRange(1.3, 1.7), 2) },
    { id: 'safe3', en: 'Safe treble', fr: 'Triple sûr', legs: pickLegs(inRange(1.3, 1.7), 3) },
    { id: 'value', en: 'Value pack', fr: 'Combo valeur', legs: pickLegs(value, 3) || pickLegs(value, 2) },
    { id: 'balanced', en: 'Balanced 4', fr: 'Équilibré 4', legs: pickLegs(inRange(1.7, 2.4), 4) },
    { id: 'lotto', en: 'Lotto 3', fr: 'Loto 3', legs: pickLegs(inRange(2.5, 5), 3) },
  ];
  return recipes.filter((r) => r.legs).map((r) => ({
    ...r,
    odds: Math.round(r.legs.reduce((x, b) => x * b.odds, 1) * 100) / 100,
    chance: r.legs.reduce((x, b) => x * b.chance, 1),
  }));
}

// ── 5. stakes: real suggestions for the player's balance ──
// A "unit" is a slice of the balance set by the risk level. Value bets are sized with a quarter
// of the Kelly formula (bet more when the scout's edge is bigger), capped at 3 units; bets the
// scout has no edge on get small fixed slices (the house edge is against them); the day's plan
// never risks more than the daily budget.
export const RISK = {
  careful: { unit: 0.01, budget: 0.05 },
  normal: { unit: 0.02, budget: 0.10 },
  bold: { unit: 0.04, budget: 0.20 },
};
const niceStake = (x) => {
  if (!(x > 0)) return 0;
  const step = x < 20 ? 1 : x < 100 ? 5 : x < 1000 ? 10 : 50;
  return Math.max(1, Math.round(x / step) * step);
};
export function suggestStake(bet, balance, risk, kind) {
  const r = RISK[risk] || RISK.normal;
  const unit = balance * r.unit;
  if (!(unit > 0)) return 0;
  let x;
  if (kind === 'parlay') x = unit * 0.5;
  else if (bet.value != null && bet.value > 0.01) {
    const kelly = (bet.chance * bet.odds - 1) / (bet.odds - 1);
    x = Math.min(3 * unit, Math.max(unit, kelly * 0.25 * balance));
  } else if (bet.odds >= 5) x = unit * 0.5;
  else x = unit;
  return Math.min(niceStake(x), Math.floor(balance));
}
// the day's plan: the best value bets (then the safest ones), one per game, stakes trimmed to the budget
export function buildPlan(scored, balance, risk, sport = 'all') {
  const r = RISK[risk] || RISK.normal;
  const budget = balance * r.budget;
  if (!(budget >= 1)) return null;
  const picks = [...rankBets(scored, 'value', sport, 6), ...rankBets(scored, 'safest', sport, 10)];
  // up to 5 picks, one per game, at their own stake; then everything scaled down together to fit
  // the budget (no crumbs: a bet under half a unit is dropped)
  const games = new Set();
  let legs = [];
  for (const b of picks) {
    if (legs.length >= 5) break;
    const g = String(b.game.id);
    if (games.has(g)) continue;
    games.add(g);
    legs.push({ ...b, stake: suggestStake(b, balance, risk) });
  }
  const want = legs.reduce((x, b) => x + b.stake, 0);
  const scale = want > budget ? budget / want : 1;
  const minStake = balance * r.unit * 0.5;
  legs = legs.map((b) => ({ ...b, stake: Math.floor(niceStake(b.stake * scale)) })).filter((b) => b.stake >= Math.max(1, minStake));
  while (legs.length && legs.reduce((x, b) => x + b.stake, 0) > budget) legs.pop();   // rounding can tip it over
  const total = legs.reduce((x, b) => x + b.stake, 0);
  if (!legs.length) return null;
  return { legs, total, budget, toWin: legs.reduce((x, b) => x + b.stake * b.odds, 0) };
}
